import { cn } from "@/lib/utils";

import type { ProcessStatus, StatusTone } from "../lib/types";

// Selo de situação e o ponto de tom que o acompanha nas listas.
//
// Os tons eram compostos por template string (`status-${tone}`), o que mantinha
// as regras invisíveis a qualquer busca literal pelo nome da classe no CSS.
// Aqui viram mapas explícitos.
//
// `status-badge` e `tone-dot` continuam no `className` como PRIMEIRO token de
// propósito: várias telas ainda posicionam o selo pelo contexto
// (`.timeline-item > .status-badge`, `.deadline-row > .status-badge`, o
// `:not(.status-badge)` de `.standalone-row`). Só as regras-base saíram do
// styles.css; essas contextuais seguem valendo até as telas migrarem.

// TUT-509 — o selo SUAVE da prévia de 14/10: sem moldura, pílula redonda,
// fundo `*-highlight` e um ponto da cor do texto na frente. O fundo e o texto
// de cada tom são os pares que `contrast.test.ts` já mede; só a moldura saiu
// (a borda continua existindo, transparente, para o selo não mudar de altura).
//
// Revisão 1 (M3) — `info` e `neutral` levam um contorno fino da cor do texto
// (35%, sombra interna, sem mexer na caixa). O fundo deles é o MESMO tom do
// realce da linha em hover no tema claro (`--primary-highlight` e
// `--surface-2` são o `#edf2f8`, 1,00:1 contra a linha) e o `--surface` no
// escuro: sem moldura, o selo perdia a silhueta. Medido contra a linha em
// hover: claro 1,95:1 (info) e 1,62:1 (neutro); escuro 2,48:1 e 1,84:1. É
// silhueta, não informação — o texto continua 10,6:1 e 5,2:1 (claro). Os
// outros três tons têm fundo próprio, distinto do realce, e ficam sem anel.
const contornoDoSelo =
  "shadow-[inset_0_0_0_1px_color-mix(in_srgb,currentColor_35%,transparent)]";
const badgeTone: Record<StatusTone, string> = {
  // No tema claro o azul do selo informativo troca para o primário — era a
  // única regra de tema no bloco.
  info: `bg-[var(--primary-highlight)] text-[#7cc8ff] [:root[data-theme='light']_&]:text-[var(--primary)] ${contornoDoSelo}`,
  success: "bg-[var(--success-highlight)] text-[var(--success)]",
  warning: "bg-[var(--warning-highlight)] text-[var(--warning)]",
  critical: "bg-[var(--critical-highlight)] text-[var(--critical)]",
  neutral: `bg-[var(--surface-2)] text-[var(--muted)] ${contornoDoSelo}`,
};

// TUT-329 — `w-fit`: dentro de grade (a coluna "Detalhes" de Documentos) o
// filho é esticado à largura da trilha, e o selo "Disponível" virava uma
// faixa maior que a palavra (dono, 27/09). O selo tem o tamanho do texto em
// qualquer pai, grade ou fluxo.
const statusBadgeBase =
  "status-badge inline-flex w-fit min-h-[1.55rem] items-center gap-[0.35rem] whitespace-nowrap rounded-full border border-transparent px-[0.6rem] py-[0.2rem] text-legenda font-semibold before:size-[0.375rem] before:flex-none before:rounded-full before:bg-current before:content-['']";

export function statusBadgeClass(tone: StatusTone, className?: string) {
  return cn(statusBadgeBase, badgeTone[tone], className);
}

export function StatusBadge({ status }: { status: ProcessStatus }) {
  return <span className={statusBadgeClass(status.tone)}>{status.label}</span>;
}

const dotTone: Record<StatusTone, string> = {
  // `neutral` não tinha regra própria: caía no cinza da base.
  neutral: "",
  info: "bg-[#4aa3ff] shadow-[0_0_8px_rgba(74,163,255,0.7)]",
  success: "bg-[var(--success)]",
  warning: "bg-[var(--warning)]",
  critical: "bg-[var(--critical)]",
};

const toneDotBase =
  "tone-dot inline-block h-[0.4rem] w-[0.4rem] flex-none rounded-full bg-[var(--muted)]";

export function toneDotClass(tone: StatusTone, className?: string) {
  return cn(toneDotBase, dotTone[tone], className);
}

export function ToneDot({ tone }: { tone: StatusTone }) {
  return <span aria-hidden="true" className={toneDotClass(tone)} />;
}
